if (!customElements.get('interior-scroll-reveal')) {
  customElements.define('interior-scroll-reveal', class extends HTMLElement {
    async connectedCallback() {
      this.stage = this.querySelector('.interior-reveal__stage');
      this.photo = this.querySelector('img');
      this.section = this.closest('.shopify-section');
      this.svg = this.querySelector('svg');
      if (!this.photo || !this.svg) return;

      this.heading = this.querySelector('h2');
      this.letters = this.querySelector('[data-interior-letters]');
      this.cutout = this.querySelector('[data-interior-cutout]');
      this.ink = this.querySelector('[data-interior-ink]');
      this.header = document.querySelector('.section-header');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.shortScreen = matchMedia('(max-height: 500px)');
      this.phoneScreen = matchMedia('(max-width: 749px)');
      this.exitStart = 0.78;
      this.portalEnd = 0.82;
      this.onUnderlayFocus = () => {
        // Keyboard navigation must never leave a focused product behind the photo.
        const end = this.getBoundingClientRect().top + scrollY - this.headerHeight + this.scrollDistance;
        if (scrollY < end) {
          window.scrollTo({ top: Math.ceil(end), behavior: 'instant' });
        }
        this.update();
      };
      this.onScroll = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
      };
      this.onPanFrame = now => this.renderPan(now);
      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.configure());
      };
      this.motion.addEventListener('change', this.onResize);
      this.shortScreen.addEventListener('change', this.onResize);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onSectionChange = () => this.onResize();
      document.addEventListener('shopify:section:load', this.onSectionChange);
      document.addEventListener('shopify:section:reorder', this.onSectionChange);
      document.addEventListener('shopify:section:unload', this.onSectionChange);

      try {
        // The letters must never reveal an unloaded image or a fallback font.
        await Promise.all([this.photo.decode(), document.fonts.ready]);
        if (!this.isConnected) return;
        this.ready = true;
        this.configure();
      } catch (_) {
        this.disable();
      }
    }

    disconnectedCallback() {
      this.motion?.removeEventListener('change', this.onResize);
      this.shortScreen?.removeEventListener('change', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:load', this.onSectionChange);
      document.removeEventListener('shopify:section:reorder', this.onSectionChange);
      document.removeEventListener('shopify:section:unload', this.onSectionChange);
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.resizeFrame);
      this.disable();
    }

    disable() {
      cancelAnimationFrame(this.panFrame);
      this.panFrame = 0;
      this.panPosition = undefined;
      this.classList.remove('is-enhanced', 'is-photo-open', 'is-motion-active');
      this.releaseUnderlay();
      document.documentElement.classList.remove('interior-story-active');
    }

    releaseUnderlay() {
      this.section?.classList.remove('is-reveal-overlay');
      if (!this.underlay) return;
      this.underlay.removeEventListener('focusin', this.onUnderlayFocus);
      this.underlay.classList.remove('interior-reveal-underlay');
      this.underlay.style.removeProperty('--interior-overlap');
      this.underlay.style.removeProperty('--interior-exit-distance');
      this.underlay.style.removeProperty('--interior-header');
      this.underlay = null;
    }

    configure() {
      if (!this.ready || this.motion.matches || this.shortScreen.matches || !CSS.supports('position', 'sticky')) {
        this.disable();
        return;
      }
      this.headerHeight = this.header?.offsetHeight || 0;
      cancelAnimationFrame(this.panFrame);
      this.panFrame = 0;
      this.panPosition = undefined;
      this.style.setProperty('--interior-header', `${this.headerHeight}px`);
      this.classList.add('is-enhanced');
      const rect = this.stage.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;
      this.scrollDistance = Math.max(this.offsetHeight - this.stage.offsetHeight, 1);
      // Include the frame's vertical overscan in the mobile image width so
      // neither end of the pan exposes an empty edge or crops unpredictably.
      const photoHeight = this.photo.parentElement.offsetHeight;
      this.panWidth = Math.max(this.width, photoHeight * this.photo.naturalWidth / this.photo.naturalHeight);
      this.style.setProperty('--interior-pan-width', `${this.panWidth}px`);
      this.releaseUnderlay();
      const next = this.section?.nextElementSibling;
      // Only pair with the adjacent circuit section. A Theme Editor reorder must
      // never turn an unrelated section into an animated underlay.
      if (next?.querySelector('.ovs-featured-circuit')) {
        this.underlay = next;
        const exitDistance = this.scrollDistance * (1 - this.exitStart);
        this.section.classList.add('is-reveal-overlay');
        next.classList.add('interior-reveal-underlay');
        next.style.setProperty('--interior-header', `${this.headerHeight}px`);
        next.style.setProperty('--interior-overlap', `${this.height + exitDistance}px`);
        next.style.setProperty('--interior-exit-distance', `${exitDistance}px`);
        next.addEventListener('focusin', this.onUnderlayFocus);
      }
      this.svg.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);
      const font = getComputedStyle(this.heading);
      this.fontSize = parseFloat(font.fontSize);
      this.letters.style.fontFamily = font.fontFamily;
      this.letters.style.fontSize = font.fontSize;
      this.letters.style.fontWeight = font.fontWeight;
      this.letters.style.letterSpacing = font.letterSpacing;
      this.letters.replaceChildren();

      const lines = [...this.querySelectorAll('[data-interior-line]')];
      for (const line of lines) {
        const bounds = line.getBoundingClientRect();
        const baseline = line.querySelector('.interior-reveal__baseline').getBoundingClientRect();
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.textContent = line.textContent.trim();
        text.setAttribute('x', bounds.left + bounds.width / 2 - rect.left);
        text.setAttribute('y', baseline.top - rect.top);
        text.setAttribute('text-anchor', 'middle');
        this.letters.appendChild(text);
      }

      try {
        const title = this.letters.getBBox();
        this.titleCenter = { x: title.x + title.width / 2, y: title.y + title.height / 2 };
        this.focus = this.findLetterInterior(font);
        // Keep SVG text below browser raster-size limits. Once we're close to a
        // stroke, its increasing weight finishes opening it across the viewport.
        this.maxScale = 64;
        // Open the letter strokes around the fixed center, without panning to a
        // letter at either edge. This covers the gap between the two lines.
        this.centerGapStroke = 2 * this.focus.distance + 4;
        this.update();
      } catch (_) {
        this.disable();
      }
    }

    findLetterInterior(font) {
      const box = this.letters.getBBox();
      const canvas = document.createElement('canvas');
      const width = canvas.width = Math.ceil(box.width) + 4;
      const height = canvas.height = Math.ceil(box.height) + 4;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.font = `${font.fontWeight} ${font.fontSize} ${font.fontFamily}`;
      ctx.fillStyle = '#000';
      // SVG character positions include its letter-spacing and kerning.
      for (const text of this.letters.children) {
        for (let i = 0; i < text.getNumberOfChars(); i++) {
          const point = text.getStartPositionOfChar(i);
          ctx.fillText(text.textContent[i], point.x - box.x + 2, point.y - box.y + 2);
        }
      }
      const pixels = ctx.getImageData(0, 0, width, height).data;
      let nearest = { distance: Infinity };
      // Find how far the nearest solid letter lies from the composition center.
      // It controls the final weight only; the zoom origin never moves sideways.
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          if (pixels[(y * width + x) * 4 + 3] <= 250) continue;
          const point = { x: box.x + x - 2, y: box.y + y - 2 };
          const distance = Math.hypot(point.x - this.titleCenter.x, point.y - this.titleCenter.y);
          if (distance < nearest.distance) nearest = { ...point, distance };
        }
      }
      if (!Number.isFinite(nearest.distance)) throw new Error('No opaque letter interior');
      return nearest;
    }

    setPan(target, animate) {
      this.panTarget = target;
      if (!animate || this.panPosition === undefined || Math.abs(target - this.panPosition) < 0.01) {
        cancelAnimationFrame(this.panFrame);
        this.panFrame = 0;
        this.panPosition = target;
        this.photo.style.setProperty('--interior-pan-x', `${target}px`);
      } else if (!this.panFrame) {
        this.panTime = performance.now();
        this.panFrame = requestAnimationFrame(this.onPanFrame);
      }
    }

    renderPan(now) {
      this.panFrame = 0;
      // Bridge discrete touch-scroll updates at the display's frame rate. A
      // short, time-based follow keeps the same path and pace at 60 or 120 Hz.
      const elapsed = Math.min(64, Math.max(0, now - this.panTime));
      this.panTime = now;
      this.panPosition += (this.panTarget - this.panPosition) * (1 - Math.exp(-elapsed / 60));
      const settled = Math.abs(this.panTarget - this.panPosition) < 0.01;
      if (settled) this.panPosition = this.panTarget;
      // Scope the animated property to the image, not the SVG and whole section.
      this.photo.style.setProperty('--interior-pan-x', `${this.panPosition}px`);
      if (!settled) this.panFrame = requestAnimationFrame(this.onPanFrame);
    }

    update() {
      this.frame = 0;
      if (!this.classList.contains('is-enhanced') || !this.focus) return;
      const rect = this.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (this.headerHeight - rect.top) / this.scrollDistance));
      const portalProgress = this.underlay ? Math.min(1, progress / this.portalEnd) : progress;
      const smooth = (start, end, value = portalProgress) => {
        const t = Math.min(1, Math.max(0, (value - start) / (end - start)));
        return t * t * (3 - 2 * t);
      };
      const copyOut = smooth(0.02, 0.16);
      const photoOpen = portalProgress >= 0.72;
      this.classList.toggle('is-photo-open', photoOpen);
      this.classList.toggle('is-motion-active', rect.top < innerHeight * 1.5 && rect.bottom > 0);
      // The mask is completely clear at 72%. Stop resizing/repainting its huge
      // invisible letters during the photo pan; restore them on reverse scroll.
      if (!photoOpen) {
        const scale = Math.exp(Math.log(2.6) * smooth(0.18, 0.5) + Math.log(this.maxScale / 2.6) * smooth(0.5, 0.92));
        const travel = smooth(0.18, 0.5);
        const x = (1 - scale) * this.titleCenter.x + (this.width / 2 - this.titleCenter.x) * travel;
        const y = (1 - scale) * this.titleCenter.y + (this.height / 2 - this.titleCenter.y) * travel;
        const transform = `matrix(${scale} 0 0 ${scale} ${x} ${y})`;
        const stroke = (this.centerGapStroke + Math.hypot(this.width, this.height) / scale) * smooth(0.68, 0.72);
        for (const layer of [this.cutout, this.ink]) {
          layer.setAttribute('transform', transform);
          layer.setAttribute('stroke-width', stroke);
        }
        this.ink.setAttribute('opacity', 1 - smooth(0.18, 0.38));
      }
      this.style.setProperty('--interior-copy-opacity', 1 - copyOut);
      const portalTiming = this.underlay ? this.portalEnd : 1;
      // Desktop moves a little deeper, continuing through the exit instead of
      // stopping when the portal opens. Keep mobile's approved pan untouched.
      const photoPush = this.phoneScreen.matches
        ? 0.045 * smooth(0.18, 1)
        : 0.11 * smooth(0.18 * portalTiming, 1, progress);
      this.photo.style.setProperty('--interior-photo-scale', 1 + photoPush);
      // Barely drift while entering through the letters: the first full frame
      // still shows the left bench. Then smoothly accelerate across the room,
      // completing almost all of the pan before the existing exit sweep.
      const panStart = 0.18 * portalTiming;
      const panRampStart = 0.72 * portalTiming;
      const panEnd = this.underlay ? this.portalEnd : 1;
      const entryPan = 0.035 * smooth(panStart, panRampStart, progress);
      const roomTime = smooth(panRampStart, panEnd, progress);
      const roomPan = roomTime + 0.18 * Math.sin(2 * Math.PI * roomTime) / (2 * Math.PI);
      const pan = entryPan + 0.965 * roomPan;
      this.setPan(this.phoneScreen.matches ? -(this.panWidth - this.width) * pan : 0,
        this.phoneScreen.matches && progress > 0 && progress < 1 && pan > 0);
      // The real next section is natively sticky underneath. Its text is
      // uncovered by the photo edge, not by a separate text animation or clone.
      const sweepTime = Math.min(1, Math.max(0, (progress - this.exitStart) / (1 - this.exitStart)));
      const sweep = this.underlay ? sweepTime * sweepTime * (2 - sweepTime) : 0;
      this.style.setProperty('--interior-sweep-x', `${-(this.width * 1.2 + this.height * 0.1) * sweep}px`);
      this.style.setProperty('--interior-sweep-y', `${-this.height * 0.035 * sweep}px`);
      this.style.setProperty('--interior-sweep-roll', `${-3.5 * sweep}deg`);
      this.style.setProperty('--interior-sweep-turn', `${-10 * sweep}deg`);
      this.dataset.pan = (this.phoneScreen.matches ? pan : 0).toFixed(4);
      this.dataset.sweep = sweep.toFixed(4);
      this.dataset.progress = progress.toFixed(4);
      document.documentElement.classList.toggle('interior-story-active', rect.top <= this.headerHeight && rect.bottom > this.headerHeight);
    }
  });
}
